import { z } from 'zod';

export const MCPOAuthSchema = z
  .object({
    clientId: z.string(),
    clientSecretName: z.string(),
    tokenUrl: z.string(),
    audience: z.string(),
    scope: z.string(),
  })
  .describe('OAuth configuration for an MCP server connection');

export type MCPOAuth = z.infer<typeof MCPOAuthSchema>;

export const MCPAuthSchema = z
  .object({
    header: z.string(),
    queryParam: z.string(),
    oauth: MCPOAuthSchema.optional(),
    secretName: z.string(),
  })
  .describe('Authentication configuration for an MCP server');

export type MCPAuth = z.infer<typeof MCPAuthSchema>;

/**
 * Accepts either a flat string array (Redux shape) or the proto wrapper
 * `{ names: string[] }` and normalises to `string[]`.
 */
const EnabledFunctionsSchema = z
  .union([
    z.array(z.string()),
    z.object({ names: z.array(z.string()) }).transform(v => v.names),
  ])
  .describe(
    'List of enabled MCP tool function names. Accepts proto shape { names: string[] } or flat string[].',
  );

export const MCPServerSchema = z
  .object({
    id: z.string().describe('Unique server identifier'),
    providerId: z.string().describe('MCP provider identifier'),
    url: z.string().describe('Server URL'),
    auth: MCPAuthSchema.optional(),
    timeout: z.number().optional().describe('Request timeout in seconds'),
    enabledFunctions: EnabledFunctionsSchema.optional(),
    lastDiscoveredAt: z
      .string()
      .optional()
      .describe('ISO timestamp of last tool discovery'),
    lastDiscoveredToolCount: z
      .number()
      .optional()
      .describe('Number of tools found during last discovery'),
  })
  .describe('Configuration for a single MCP server');

export type MCPServer = z.infer<typeof MCPServerSchema>;

export const MCPSavedProfileSchema = z
  .object({
    providerId: z.string().describe('MCP provider identifier'),
    url: z.string().describe('Server URL'),
    auth: MCPAuthSchema.optional(),
    timeout: z.number().optional().describe('Request timeout in seconds'),
  })
  .describe('Saved connection profile for an MCP provider');

export type MCPSavedProfile = z.infer<typeof MCPSavedProfileSchema>;

export const MCPConfigStateSchema = z
  .object({
    servers: z
      .object({
        ids: z.array(z.string()).default([]),
        entities: z.record(z.string(), MCPServerSchema).default({}),
      })
      .default({ ids: [], entities: {} }),
    savedProfiles: z
      .object({
        ids: z.array(z.string()).default([]),
        entities: z.record(z.string(), MCPSavedProfileSchema).default({}),
      })
      .default({ ids: [], entities: {} }),
  })
  .describe('Root state shape for the mcpConfig Redux slice (entity adapter)');

export type MCPConfigState = z.infer<typeof MCPConfigStateSchema>;
